import { Injectable } from '@angular/core';
import { RoleType } from '../constants-types/Enums';
import { PI } from '../constants-types/PI.model';
import {
    BulkUpdateEntry,
    BulkUpdateType
} from '../core-data-model/BulkUpdateApi.connectinterface';
import {
    BulkUpdatePost,
    BulkUpdateServer
} from '../core-data-model/BulkUpdateApi.serverinterface';
import {
    BulkUpdateTransformers,
    PersonJobUpdate
} from '../core-services/BulkUpdateApi.datatransform';
import { hasPermissionLevel } from '../core-services/HasPermissionLevel.decorator';
import {
    FeatureFlags,
    FeatureFlagsService,
    ModuleAccessService
} from '../system-services';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';

@Injectable({
    providedIn: 'root'
})
export class BulkUpdateApi {
    constructor(
        private readonly api: ApiService,
        private readonly userProfile: UserProfileSettingsService,
        private readonly transform: BulkUpdateTransformers,
        private readonly moduleAccess: ModuleAccessService,
        private readonly featureFlag: FeatureFlagsService
    ) {}

    @hasPermissionLevel(RoleType.User)
    bulkUpdateSeparateJobs(
        updates: PersonJobUpdate[],
        companyId = this.userProfile.currentCompany
    ) {
        const updateType = BulkUpdateType.UpdateJob;
        return this.api.post<BulkUpdatePost, BulkUpdateServer>(
            this.endpointFromUpdateType(updateType, companyId),
            this.transform.transformBulkUpdatePersonJob(updateType, updates)
        );
    }

    bulkUpdate(
        updateType: BulkUpdateType,
        updateEntries: BulkUpdateEntry[],
        destinationId?: string,
        companyId = this.userProfile.currentCompany
    ) {
        const url = this.endpointFromUpdateType(updateType, companyId);
        if (
            updateType === BulkUpdateType.RemoveFromTeam ||
            updateType === BulkUpdateType.AddToTeam
        ) {
            const { teamsUpdateUrl, designApiUrl } =
                this.updateTeamsEndpoint(url);
            return this.api.post<BulkUpdatePost, BulkUpdateServer>(
                teamsUpdateUrl,
                this.transform.transformBulkUpdatePost(
                    updateType,
                    updateEntries,
                    destinationId
                ),
                designApiUrl
            );
        }
        return this.api.post<BulkUpdatePost, BulkUpdateServer>(
            this.endpointFromUpdateType(updateType, companyId),
            this.transform.transformBulkUpdatePost(
                updateType,
                updateEntries,
                destinationId
            )
        );
    }

    private endpointFromUpdateType(
        updateType: BulkUpdateType,
        companyId: string
    ): string {
        if (
            updateType === BulkUpdateType.UpdateJob ||
            updateType === BulkUpdateType.UpdateType ||
            updateType === BulkUpdateType.RemoveAssociatedJob
        ) {
            return `/api/v3/persons/${companyId}/synchronousbatch`;
        }
        if (
            updateType === BulkUpdateType.RemoveFromTeam ||
            updateType === BulkUpdateType.AddToTeam
        ) {
            return `/api/v4/companies/${companyId}/teams/synchronousbatchteamitems`;
        }
        return `/api/v2/companies/${companyId}/peopleandjobs/synchronousbatch`;
    }

    /**
     * Adjust teams endpoint URL based on design feature flag.
     * If the URL contains '/teams' and the feature is enabled, return the updated URL
     * (by replacing /api/v4/ with /api/v5/) together with the design API host.
     */
    private updateTeamsEndpoint(url: string): {
        teamsUpdateUrl: string;
        designApiUrl?: string;
    } {
        const designEnabled =
            this.moduleAccess.hasDesign2 &&
            this.featureFlag.getByFlag(FeatureFlags.enableDesign2TeamEndpoints);
        if (designEnabled) {
            return {
                teamsUpdateUrl: url.replace('/api/v4/', '/api/v5/'),
                designApiUrl: PI.appSettings.designApiUrl
            };
        }
        return {
            teamsUpdateUrl: url,
            designApiUrl: null
        };
    }
}
